import { store } from 'store';
import { MarketplacePetSize } from 'types/Marketplace';
import { Pet, PetType, PetTypeIds } from 'types/Pet';
import { Service, ServiceDuration } from 'types/Service';

export const isServiceAvailableForPet = (service: Service, pet: Pet) => {
    const marketplace = store.getState().marketplace.data;

    // Marketplace has pet type active
    if (pet.type.id === PetTypeIds.CAT && !marketplace.catsEnabled) return false;
    if (pet.type.id === PetTypeIds.DOG && !marketplace.dogsEnabled) return false;
    if (pet.type.id === PetTypeIds.EXOTIC && !marketplace.exoticsEnabled) return false;

    //   Has fixed price
    if (service.fixedPrice.length > 0) {
        const hasValidFixedPrice = service.fixedPrice.some(fixedPrice => {
            return (
                fixedPrice.petType.id === pet.type.id && fixedPrice.price && fixedPrice.price > 0
            );
        });

        // Has variable duration
        if (service.variableDuration.length > 0) {
            return hasValidFixedPrice && isVariableDurationServiceAvailableForPet(service, pet);
        }

        return hasValidFixedPrice;
    }

    // Has variable price
    if (service.variablePrice) {
        const hasValidVariablePrice = service.variablePrice.prices.some(variablePrice => {
            return (
                variablePrice.petType?.id === pet.type.id &&
                variablePrice.petSize?.id === pet.size.id &&
                variablePrice.price &&
                variablePrice.price > 0
            );
        });

        // Has variable duration
        if (service.variableDuration.length > 0) {
            return hasValidVariablePrice && isVariableDurationServiceAvailableForPet(service, pet);
        }

        return hasValidVariablePrice;
    }

    // Edge cases
    return false;
};

export const getServicePriceByPet = (service: Service, pet: Pet) => {
    let priceResult = 0;

    if (service.fixedPrice.length > 0) {
        priceResult = service.fixedPrice
            .filter(price => price.petType?.id === pet.type?.id)
            .map(p => p.price)
            .reduce((min, current) => Math.min(min || 0, current || 0), Infinity) as number;
    }

    if (service.variablePrice) {
        if (
            (service.variablePrice.variables.petSizeVariable &&
                service.variablePrice.variables.hairLengthVariable) ||
            service.variablePrice.variables.hairLengthVariable
        ) {
            priceResult = service.variablePrice.prices
                .filter(
                    price =>
                        price.petType?.id === pet.type?.id &&
                        price.petSize?.id === pet.size.id &&
                        (price.hairLength?.id === pet.hairLength?.id ||
                            pet.hairLength === undefined)
                )
                .map(p => p.price)
                .reduce((min, current) => Math.min(min || 0, current || 0), Infinity) as number;
        } else if (
            service.variablePrice.variables.petSizeVariable &&
            !service.variablePrice.variables.hairLengthVariable
        ) {
            priceResult = service.variablePrice.prices
                .filter(
                    price => price.petSize?.id === pet.size.id && price.petType?.id === pet.type?.id
                )
                .map(p => p.price)
                .reduce((min, current) => Math.min(min || 0, current || 0), Infinity) as number;
        }
    }

    if (!pet.type.id) {
        priceResult = 0;
    }

    return priceResult;
};

const isVariableDurationServiceAvailableForPet = (service: Service, pet: Pet) => {
    return service.variableDuration.some(
        variableDuration => variableDuration.petSize.id === pet.size.id
    );
};

export const getServiceDurationByPet = (
    service: Service,
    petType: PetType,
    petSize: MarketplacePetSize
): number | false => {
    const marketplace = store.getState().marketplace.data;

    // Marketplace has pet type active
    if (petType.id === PetTypeIds.CAT && !marketplace.catsEnabled) return false;
    if (petType.id === PetTypeIds.DOG && !marketplace.dogsEnabled) return false;
    if (petType.id === PetTypeIds.EXOTIC && !marketplace.exoticsEnabled) return false;

    let serviceDuration: ServiceDuration | undefined;

    // Variable duration
    if (service.variableDuration.length > 0) {
        const variableDurationForPet = service.variableDuration.find(
            duration => duration.petSize.id === petSize.id
        );

        if (!variableDurationForPet) {
            return false;
        }

        serviceDuration = variableDurationForPet.duration;
    }

    // Fixed duration
    if (service.durationHours != null && service.durationMinutes != null) {
        serviceDuration = {
            hours: service.durationHours,
            minutes: service.durationMinutes
        };
    }

    if (!serviceDuration) return false;

    return getServiceDurationInMinutes(serviceDuration);
};

export const getServiceDurationInMinutes = (serviceDuration: ServiceDuration) => {
    const hoursInMinutes = serviceDuration.hours * 60;

    return hoursInMinutes + serviceDuration.minutes;
};
